import { useMessages } from '@app/context/MessageContext';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import {
  useDismissKlausMergeOfferMutation,
  useLinkKlausOrgMutation,
} from '@dieterApi/user/useKlausMergeOfferMutations';
import { Button, NotificationModal } from '@components/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Merge-Angebot nach dem Login (Klaus ↔ Dieter Sync, Flow F).
 *
 * Der User hat sich mit seiner Klaus-Identität bei Dieter angemeldet und besitzt dort eine
 * Organisation, die noch mit keiner Dieter-Firma verknüpft ist. "Ja" verknüpft die aktive
 * Firma mit der gewählten Org (Dieter führt die Mitgliedschaften, Klaus zieht nach). "Nein"
 * wird am User gemerkt; verknüpfen geht später weiterhin. Beides lässt den Login unberührt.
 */
export function KlausMergeOfferMessage() {
  const { t } = useTranslation();
  const { klausmerge } = useMessages();
  const { user } = useUserWithCompany();
  const [linkKlausOrg, { loading: linking }] = useLinkKlausOrgMutation();
  const [dismiss, { loading: dismissing }] = useDismissKlausMergeOfferMutation();
  const [error, setError] = useState<string | null>(null);

  const orgs = user?.klausMergeOffer?.orgs ?? [];
  const [selected, setSelected] = useState<string | undefined>(orgs[0]?.id);

  useEffect(() => {
    // Context = die angebotenen Orgs: ändert sich das Angebot (neue Org bei Klaus), zählt es als neue Nachricht.
    klausmerge.setContext({ name: 'klausmerge', orgs: orgs.map((o) => o.id) });
  }, [orgs.map((o) => o.id).join(',')]);

  useEffect(() => {
    if (!selected && orgs[0]) setSelected(orgs[0].id);
  }, [orgs, selected]);

  const busy = linking || dismissing;

  const handleLink = async () => {
    if (!selected) return;
    setError(null);
    try {
      await linkKlausOrg({ variables: { klausOrgId: selected } });
      klausmerge.close();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleDismiss = async () => {
    setError(null);
    try {
      await dismiss();
    } finally {
      klausmerge.close();
    }
  };

  const companyName = user?.company?.name ?? '';

  return (
    <NotificationModal open={klausmerge.isOpen} onClose={handleDismiss} closeButtonColor="white" unsolicited>
      <div className="flex flex-col p-[30px]">
        <div className="bg-klaus-root -mx-[40px] px-9 py-7 -mt-[40px] flex flex-col items-center text-center gap-4">
          <div className="flex items-end justify-center gap-6">
            <img src="/assets/images/dieter-hello.png" alt="Dieter" className="h-[120px]" />
            <img src="/assets/images/klaus/klaus-waving.png" alt="Klaus" className="h-[120px]" />
          </div>
          <span className="text-white font-medium text-lg">
            <span className="block text-2xl font-bold">{t('klaus.merge.title')}</span>
            {t('klaus.merge.subtitle')}
          </span>
        </div>

        <p className="mt-5">
          {orgs.length === 1
            ? t('klaus.merge.intro_single', { org: orgs[0].name, company: companyName })
            : t('klaus.merge.intro_multi', { company: companyName })}
        </p>

        {orgs.length > 1 && (
          <fieldset className="flex flex-col gap-2 mt-3" data-testid="klaus-merge-orgs">
            {orgs.map((org) => (
              <label key={org.id} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="klaus-merge-org"
                  value={org.id}
                  checked={selected === org.id}
                  onChange={() => setSelected(org.id)}
                />
                <span>{org.name}</span>
              </label>
            ))}
          </fieldset>
        )}

        <p className="mt-3 text-sm text-gray-500">{t('klaus.merge.hint')}</p>

        {error && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <div className="pointer-events-auto bg-white my-5">
          <div className="flex flex-col items-center text-center gap-3">
            <Button onClick={handleLink} disabled={busy || !selected} data-testid="klaus-merge-link">
              {t('klaus.merge.yes')}
            </Button>
            <button
              onClick={handleDismiss}
              disabled={busy}
              className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
              data-testid="klaus-merge-dismiss"
            >
              {t('klaus.merge.no')}
            </button>
          </div>
        </div>
      </div>
    </NotificationModal>
  );
}
